"use client";

import { OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useEffect, useState } from 'react';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';
import { apiOfferBanners } from '@/apis/offers';
import Spinner from 'react-bootstrap/Spinner';
import Banner from './Banner';

interface Props {
    bannerResponse: OffersOffer[];
    domain: string;
    mer_slug: string;
    slug_type: string;
    merchantId: string;
    companyId: string;
    pagination: PaginationType;
}

const VerticalOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, merchantId, companyId, pagination }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([]);
    const [page, setPage] = useState(2);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const initialFiltered = filterOfferBanners(bannerResponse, 20, 2000, 20, 2000);
        setBanners(initialFiltered);
        if (pagination?.next_page === null) {
            setHasMore(false);
        }
    }, [bannerResponse, pagination?.next_page]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const res = await apiOfferBanners(merchantId, companyId, page);
            const newBanners = filterOfferBanners(res.data?.offers || [], 20, 2000, 20, 2000);

            setBanners(prev => [...prev, ...newBanners]);
            if (res?.data?.pagination?.next_page) {
                setPage(prev => prev + 1);
                setIsExpanded(true);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error loading more vertical banners", error);
        } finally {
            setLoading(false);
        }
    };

    const showLess = () => {
        const initialFiltered = filterOfferBanners(bannerResponse, 20, 2000, 20, 2000);
        setBanners(initialFiltered);
        setPage(2);
        setHasMore(true);
        setIsExpanded(false);
    };

    if (banners?.length === 0) return null;

    return (
        <div className="relative">
            {/* Premium Card Container */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E7EB] overflow-hidden transition-all duration-300 hover:shadow-md">
                
                {/* Subtle Background Gradient */}
                <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-[#F97316]/5 to-transparent rounded-full blur-2xl" />
                
                {/* Header */}
                <div className="relative flex items-center justify-between mb-6 pb-4 border-b border-[#E5E7EB]">
                    <h4 className="text-lg font-black text-[#111827]">
                        Featured Banner Offers
                    </h4>
                </div>

                {/* Banners Grid */}
                <div className="relative flex flex-col gap-4 mb-6">
                    {banners?.map((offer_data, i) => {
                        const dimension = getBannerDimensions(offer_data);
                        return (
                            <Banner
                                key={i}
                                data={offer_data}
                                height={dimension?.height}
                                mer_slug={mer_slug}
                                slug_type={slug_type}
                                domain={domain}
                                width={dimension?.width}
                            />
                        );
                    })}
                </div>

                {/* Action Buttons */}
                <div className={`flex items-center ${isExpanded ? 'justify-between' : 'justify-center'} gap-3`}>
                    {hasMore && (
                        <button 
                            onClick={loadMore} 
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#EA580C] text-white font-bold text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Spinner size="sm" animation="border" className="border-white" />
                                    <span>Loading...</span>
                                </>
                            ) : (
                                <span>Show More Banners</span>
                            )}
                        </button>
                    )}
                    {isExpanded && (
                        <button 
                            onClick={showLess}
                            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white hover:bg-[#F8FAFC] text-[#4B5563] hover:text-[#111827] font-bold text-sm rounded-lg border border-[#E5E7EB] transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                        >
                            <span>Show Less</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerticalOfferBanner;
